import * as Api from '@ViewModels';
import type { PhoneCallView } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import { IPhoneCallFilterCriteria, PhoneCallFilterCriteriaProperty } from '../../viewModels/phonecall';

/** Raw input strings ('' = unbounded); scores are 0–100, length is minutes, dates `YYYY-MM-DD`. */
export interface RepCardFilters {
	minScore: string;
	maxScore: string;
	minMinutes: string;
	maxMinutes: string;
	startDate: string;
	endDate: string;
}

export const EMPTY_REP_CARD_FILTERS: RepCardFilters = {
	minScore: '',
	maxScore: '',
	minMinutes: '',
	maxMinutes: '',
	startDate: '',
	endDate: '',
};

export const hasActiveRepCardFilters = (filters: RepCardFilters): boolean =>
	Object.values(filters).some(value => value.trim() !== '');

const MS_PER_MINUTE = 60_000;

const toInt = (value: string): number | null => {
	const trimmed = value.trim();
	if (trimmed === '') {
		return null;
	}
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

const minutesToMs = (value: string): number | null => {
	const trimmed = value.trim();
	if (trimmed === '') {
		return null;
	}
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? Math.round(parsed * MS_PER_MINUTE) : null;
};

/**
 * Only `User` + `Date` are sent — the backend rejects `AdherenceScore` / `Duration` criteria, so score
 * and length are applied client-side via {@link applyClientCallFilters}.
 */
export function buildRepCallCriteria(repId: string, filters: RepCardFilters): IPhoneCallFilterCriteria[] {
	const criteria: IPhoneCallFilterCriteria[] = [{ property: PhoneCallFilterCriteriaProperty.User, value: repId }];

	if (filters.startDate) {
		criteria.push({
			property: PhoneCallFilterCriteriaProperty.Date,
			op: Api.FilterOperator.Gt,
			value: new Date(`${filters.startDate}T00:00:00`).toISOString(),
		});
	}
	if (filters.endDate) {
		criteria.push({
			property: PhoneCallFilterCriteriaProperty.Date,
			op: Api.FilterOperator.Lt,
			value: new Date(`${filters.endDate}T23:59:59.999`).toISOString(),
		});
	}

	return criteria;
}

export const hasClientCallFilters = (filters: RepCardFilters): boolean =>
	[filters.minScore, filters.maxScore, filters.minMinutes, filters.maxMinutes].some(value => value.trim() !== '');

/** Bounds are inclusive; a not-scorable call is dropped whenever any score bound is set. */
export function applyClientCallFilters(calls: PhoneCallView[], filters: RepCardFilters): PhoneCallView[] {
	const minScore = toInt(filters.minScore);
	const maxScore = toInt(filters.maxScore);
	const minMs = minutesToMs(filters.minMinutes);
	const maxMs = minutesToMs(filters.maxMinutes);

	if (minScore == null && maxScore == null && minMs == null && maxMs == null) {
		return calls;
	}

	return calls.filter(({ phoneCall }) => {
		if (minScore != null || maxScore != null) {
			const score = phoneCall.coachingAdherenceResult?.score;
			if (!isScorable(score)) {
				return false;
			}
			if (minScore != null && (score as number) < minScore) {
				return false;
			}
			if (maxScore != null && (score as number) > maxScore) {
				return false;
			}
		}
		const durationMs = phoneCall.duration ?? 0;
		if (minMs != null && durationMs < minMs) {
			return false;
		}
		if (maxMs != null && durationMs > maxMs) {
			return false;
		}
		return true;
	});
}

/** A `coachingAdherenceResult.score` of `-1` means "no transcript / not scorable" — never a real 0. */
export const isScorable = (score?: number | null): boolean => typeof score === 'number' && score >= 0;

export const formatScore = (score?: number | null): string =>
	isScorable(score) ? String(Math.round(score as number)) : 'N/A';

export const formatSuggestionAdherence = (value?: number | null): string | null =>
	typeof value === 'number' && value >= 0 ? `${Math.round(value)}%` : null;

export type ScorePillColor = 'gray' | 'green' | 'teal' | 'yellow' | 'red';

export const scorePillColor = (score?: number | null): ScorePillColor => {
	if (!isScorable(score)) {
		return 'gray';
	}
	const value = score as number;
	if (value >= 80) {
		return 'green';
	}
	if (value >= 60) {
		return 'teal';
	}
	if (value >= 40) {
		return 'yellow';
	}
	return 'red';
};
